import React, { useState } from 'react';
import { Upload } from '@/components/ui/Upload';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import ImgCropBase from 'antd-img-crop';
const ImgCrop = ImgCropBase as React.FC<
  import('antd-img-crop').ImgCropProps & { children?: React.ReactNode }
>;
import { useDispatch, useSelector } from 'react-redux';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import ProfileForm from './ProfileForm';
import { apiService } from '../../../utils/api';

interface ProfileEditModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const ProfileEditModal: React.FC<ProfileEditModalProps> = ({ visible, onClose, onSubmit }) => {
  const [reset, setReset] = useState(false);
  const [error, setError] = useState<any>(null);
  const [submit, setSubmit] = useState(false);
  const user = useSelector((state: any) => state.auth.user);
  const [progress, setProgress] = useState(0);

  const dispatch = useDispatch();

  const formSubmit = async (data: Record<string, any>) => {
    setSubmit(false);
    try {
      const { data: response } = await apiService.patch(`/api/v1/user/${user?.id}/`, data);
      if (response) {
        dispatch({ type: 'UPDATE_USER_SUCCESSFULLY', data: response });
        onSubmit();
      }
    } catch (error) {
      setError(error);
    }
  };

  function beforeUpload(file: any) {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      toast.error('You can only upload JPG/PNG file!');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      toast.error('Image must smaller than 2MB!');
    }
    return isJpgOrPng && isLt2M;
  }

  const uploadImage = async (options: any) => {
    const { onSuccess, onError, file, onProgress } = options;

    const fmData = new FormData();
    const config = {
      headers: { 'content-type': 'multipart/form-data' },
      onUploadProgress: (event: any) => {
        const percent = Math.floor((event.loaded / event.total) * 100);
        setProgress(percent);
        if (percent === 100) {
          setTimeout(() => setProgress(0), 1000);
        }
        onProgress({ percent: (event.loaded / event.total) * 100 });
      },
    };
    fmData.append('image_url', file);
    try {
      const res = await apiService.patch(`/api/v1/user/${user?.id}/`, fmData, config);
      if (res) {
        dispatch({ type: 'UPDATE_USER_SUCCESSFULLY', data: res.data });
      }
      onSuccess('Ok');
    } catch (err) {
      onError({ err });
    }
  };

  const imageCropRender = () => (
    <>
      <ImgCrop shape="round">
        <Upload
          customRequest={uploadImage}
          listType="picture-card"
          fileList={[{ url: user?.image_url, uid: '-1', name: 'avatar' }]}
          beforeUpload={beforeUpload}
        >
          <div className="image-crop-wrapper">{'Změnit avatara'}</div>
        </Upload>
      </ImgCrop>
      {progress > 0 ? <Progress value={progress} /> : null}
    </>
  );

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editace uživatele</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {imageCropRender()}
          <ProfileForm
            formProps={user}
            error={error}
            submit={formSubmit}
            submitOutside={submit}
            reset={reset}
            resetConfirm={() => setReset(false)}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            data-testid="profile-edit-close-btn"
            key={1}
            variant="outline"
            onClick={() => onClose()}
          >
            Zavřít
          </Button>
          <Button data-testid="profile-edit-save-btn" key={2} onClick={() => setSubmit(true)}>
            Uložit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileEditModal;
